/**
 * An assistant time write, entry by entry: what it was and what it is now.
 * Rendered under the tool row (the change card beside it carries Undo).
 */
import { Clock } from "lucide-react";
import type { FC } from "react";

import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";

import type { TimeEntry } from "../types";
import { formatHours } from "../utils";

export type TimeEntryChange = {
  before: TimeEntry | null;
  after: TimeEntry | null;
};

type Props = {
  changes: TimeEntryChange[];
  className?: string;
};

const Was: FC<{ show: boolean; children: string }> = ({ show, children }) =>
  show ? <span className="text-fg-3 line-through">{children}</span> : null;

const Row: FC<{ change: TimeEntryChange }> = ({ change }) => {
  const { before, after } = change;
  const entry = after ?? before;
  if (!entry) return null;
  const discarded = after?.status === "discarded";
  const hoursChanged = Boolean(
    before && after && before.duration_hours !== after.duration_hours
  );
  const caseChanged = Boolean(
    before && after && before.case_label !== after.case_label
  );
  const narrativeChanged = Boolean(
    before && after && before.narrative !== after.narrative
  );
  return (
    <li
      data-testid="time-entry-change"
      data-entry-id={entry.id}
      className={cn(
        "flex min-w-0 flex-col gap-1 px-3 py-2.5",
        discarded && "opacity-60"
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
        <span className="tabular-nums font-medium">
          <Was show={hoursChanged}>
            {formatHours(before?.duration_hours ?? 0)}
          </Was>
          {hoursChanged ? " " : null}
          <span className={cn(discarded && "line-through")}>
            {formatHours(entry.duration_hours)}
          </span>
        </span>
        <span className="text-fg-3">·</span>
        <span className="min-w-0 truncate">
          <Was show={caseChanged}>{before?.case_label || "No Case"}</Was>
          {caseChanged ? " " : null}
          {entry.case_label || <span className="text-warn">Needs a Case</span>}
        </span>
        <span className="text-fg-3">{entry.work_date}</span>
        <StatusBadge className="ml-auto">
          {before ? entry.status_label : "New"}
        </StatusBadge>
      </div>
      {entry.narrative || narrativeChanged ? (
        <div className="min-w-0 text-base text-fg-2">
          {narrativeChanged ? (
            <div className="text-fg-3 line-through">{before?.narrative}</div>
          ) : null}
          <div>{entry.narrative}</div>
        </div>
      ) : null}
      {after?.write_down && hoursChanged ? (
        <div className="text-sm text-fg-3">
          Write-down from {formatHours(after.write_down.captured_hours)}
          {after.write_down.note ? `: ${after.write_down.note}` : ""}
        </div>
      ) : null}
    </li>
  );
};

export const TimeEntryCard: FC<Props> = ({ changes, className }) => {
  const rows = changes.filter((c) => c.after || c.before);
  if (!rows.length) return null;
  return (
    <div
      data-testid="time-entry-card"
      className={cn(
        "flex min-w-0 flex-col overflow-hidden rounded-xl bg-elevated text-base ring-1 ring-line-2",
        className
      )}
    >
      <div className="flex h-8 items-center gap-1.5 border-b border-line-1 px-3 text-sm text-fg-3">
        <Clock className="size-3.5" /> Your Time
      </div>
      <ul className="divide-y divide-line-1">
        {rows.map((c, i) => (
          <Row key={(c.after ?? c.before)?.id ?? i} change={c} />
        ))}
      </ul>
    </div>
  );
};

/** The `time_entries` of a time tool's output, when it has them. */
export const timeEntryChangesOf = (
  output: unknown
): TimeEntryChange[] | null => {
  if (typeof output !== "object" || output === null) return null;
  const raw = (output as { time_entries?: unknown }).time_entries;
  if (!Array.isArray(raw)) return null;
  const out = raw.filter(
    (r): r is TimeEntryChange =>
      typeof r === "object" && r !== null && ("after" in r || "before" in r)
  );
  return out.length ? out : null;
};
